/**
 * @fileoverview Confirm-gated delete dialog for Library items.
 *
 * Unsynced items get the soft-delete copy (restorable by an
 * administrator). Synced folders add a default-ON "also move to the
 * {OneDrive|SharePoint} recycle bin" checkbox — unchecking it warns that
 * inbound sync will resurrect the folder. Documents always default to a
 * local-only delete; when the job has an active drive connection the same
 * checkbox offers a remote delete for drive-synced files.
 */

import { useEffect, useId, useState } from "react";
import { useConvex } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ConvexError } from "convex/values";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import type { LibraryMenuItem } from "./itemMenus";

interface DeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: LibraryMenuItem[];
  /** "OneDrive" / "SharePoint" when the job has an active drive connection. */
  providerName: string | null;
}

/**
 * AlertDialog confirming a delete of the given selection. Folders delete
 * via `deleteFolder` (alsoDeleteRemote is REQUIRED there); documents via
 * `deleteDocument`/`bulkDeleteDocuments` with the optional flag.
 */
export function DeleteConfirmDialog({
  open,
  onOpenChange,
  items,
  providerName,
}: DeleteConfirmDialogProps) {
  const convex = useConvex();
  const checkboxId = useId();
  const [isDeleting, setIsDeleting] = useState(false);

  const folders = items.filter((item) => item.kind === "folder");
  const documents = items.filter((item) => item.kind === "file");
  const syncedFolders = folders.filter((folder) => folder.isSynced);
  const hasSyncedFolder = syncedFolders.length > 0;

  // Synced folders default the remote delete ON (keeping Morgan and the
  // drive aligned); document-only deletes default OFF.
  const [alsoDeleteRemote, setAlsoDeleteRemote] = useState(hasSyncedFolder);

  useEffect(() => {
    if (open) setAlsoDeleteRemote(hasSyncedFolder);
  }, [open, hasSyncedFolder]);

  const showCheckbox =
    providerName !== null && (hasSyncedFolder || documents.length > 0);

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    // The checkbox is scoped by mode: in synced-folder mode its copy names
    // the folder's recycle bin, so its value applies ONLY to deleteFolder —
    // documents in a mixed selection are always deleted from Morgan only.
    // Documents get their own checkbox in the doc-only mode.
    const folderRemote = hasSyncedFolder && showCheckbox && alsoDeleteRemote;
    const documentRemote = !hasSyncedFolder && showCheckbox && alsoDeleteRemote;
    try {
      for (const folder of folders) {
        if (!folder.folderId) continue;
        await convex.mutation(
          api.jobs.gatherInformation.folders.mutations.deleteFolder,
          {
            folderId: folder.folderId as Id<"folders">,
            alsoDeleteRemote: folderRemote,
          },
        );
      }
      const documentIds = documents
        .map((doc) => doc.documentId)
        .filter((id): id is Id<"documents"> => id !== undefined);
      if (documentIds.length === 1) {
        await convex.mutation(
          api.jobs.gatherInformation.documents.mutations.deleteDocument,
          { documentId: documentIds[0], alsoDeleteRemote: documentRemote },
        );
      } else if (documentIds.length > 1) {
        await convex.mutation(
          api.jobs.gatherInformation.documents.mutations.bulkDeleteDocuments,
          { documentIds, alsoDeleteRemote: documentRemote },
        );
      }
      toast.success(
        `Deleted ${items.length} item${items.length === 1 ? "" : "s"}`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof ConvexError && typeof err.data === "string"
          ? err.data
          : "Failed to delete items",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const firstSyncedName = syncedFolders[0]?.name ?? "";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete {items.length} item{items.length === 1 ? "" : "s"}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {items.length === 1 ? (
              <>
                <span className="font-medium text-foreground">
                  {items[0].name}
                </span>{" "}
                will be removed from the Library. Deleted items can be restored
                by an administrator.
              </>
            ) : (
              <>
                The selected items will be removed from the Library. Deleted
                items can be restored by an administrator.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {showCheckbox && providerName && (
          <div className="space-y-2 rounded-md border bg-muted/40 p-3">
            <label
              htmlFor={checkboxId}
              className="flex items-start gap-2 text-sm"
            >
              <Checkbox
                id={checkboxId}
                checked={alsoDeleteRemote}
                onCheckedChange={(checked) =>
                  setAlsoDeleteRemote(checked === true)
                }
                className="mt-0.5"
              />
              <span>
                {hasSyncedFolder
                  ? `Also move to the ${providerName} recycle bin`
                  : "Also delete from the connected drive if this file is synced"}
              </span>
            </label>
            {hasSyncedFolder &&
              (alsoDeleteRemote ? (
                <p className="text-xs text-muted-foreground">
                  This will also move “{firstSyncedName}” and its files to the{" "}
                  {providerName} recycle bin.
                </p>
              ) : (
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  The folder will reappear at the next sync because it still
                  exists on the drive.
                </p>
              ))}
            {hasSyncedFolder && documents.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Documents selected alongside the folder are deleted from Morgan
                only.
              </p>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={isDeleting}
            onClick={() => void handleDelete()}
          >
            {isDeleting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
